import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BoxFull, TransactionalSubheader, Dropdown, InputText, Button, Accordion } from "iuvity-ui-components";
import { Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { useApplications } from "../../hooks/useApplications";
import { useTenants } from "../../hooks/useTenants";
import { useVersions } from "../../hooks/useVersions";
import { AppContext } from "../../context";
import { Controller, useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { ApplicationCreatorSchema } from "../../validations/schemas";
import { APPLICATION_STATES, REVALIDATE_FORM_PROPS } from "../../constants";
import { NewApplication } from "../../interfaces";
import { AppDataTableActions } from "../../components/applications/AppDatatableActions";
import { InformationApplication } from "../../components/applications/InformationApplication";
import { formatDateToLocale } from "../../helpers/formatDate";

export const applicationCreatorDefaultValues = {
  id: "",
  name: "",
  status: "",
  segment: "",
  createdat: "",
  currentversion: "",
  updatedat: "",
};

export const ApplicationsConfigurationPage = () => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedApplication, location, language },
    setAppState,
  } = useContext(AppContext);

  const [visibleModelInformationApp, setVisibleModelInformationApp] = useState<boolean>(false);

  const {
    control,
    getValues,
    setValue,
    reset,
    setError,
    trigger,
    formState: { errors, isDirty, isValid, defaultValues },
  } = useForm({
    criteriaMode: 'all',
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: {
      id: selectedApplication?.id,
      name: selectedApplication?.name,
      segment: selectedApplication?.segment,
      tenantid: selectedApplication?.tenantid,
      currentversion: selectedApplication?.currentversion,
      status: selectedApplication?.status,
      createdat: !!selectedApplication?.createdat ? formatDateToLocale(selectedApplication?.createdat, language, true) : '',
      updatedat: !!selectedApplication?.updatedat ? formatDateToLocale(selectedApplication?.updatedat, language, true) : '',
    },
  });

  // ACTUALIZA LOS CAMPOS DE FECHA CON EL CAMBIO DE IDIOMA
  useEffect(() => {
    setValue('createdat', !!selectedApplication?.createdat ? formatDateToLocale(selectedApplication?.createdat, language, true) : '');
    setValue('updatedat', !!selectedApplication?.updatedat ? formatDateToLocale(selectedApplication?.updatedat, language, true) : '');
  }, [language, selectedApplication?.createdat, selectedApplication?.updatedat, setValue]);

  const { data: applicationsData, applicationAction } = useApplications("DATA");
  const { data: tenantsData } = useTenants("DATA");
  const { data: versionsData } = useVersions("DATA");

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const formOptions: any = useMemo(() => {
    if (applicationsData && tenantsData && versionsData) {
      return {
        applications: applicationsData
          .map((app: any) => ({
            label: app.name,
            value: app.id,
            tenantid: app.tenantid,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        segments: applicationsData
          .reduce(function (result: any, element: any) {
            if (
              ![
                ...new Set(result.map((segmentData: any) => segmentData.value)),
              ].includes(element.segment)
            ) {
              result.push({
                label: element.segment,
                value: element.segment,
              });
            }
            return result;
          }, [])
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        tenants: tenantsData
          .map((tenant: any) => ({
            label: tenant.name,
            value: tenant.id,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        versions: versionsData
          .map((version: any) => ({
            label: version.name,
            value: version.name,
          }))
          .sort((a: any, b: any) => b.label.localeCompare(a.label)),
      };
    } else {
      return;
    }
  }, [applicationsData, tenantsData, versionsData]);

  useEffect(() => {
    if (formOptions) {
      setAppState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    } else {
      setAppState((prev) => ({
        ...prev,
        isLoading: true,
      }));
    }
  }, [formOptions, setAppState]);

  const onSave = async () => {
    const formValues = getValues();

    const actionStatus = await applicationAction("UPDATE", [
      {
        ...formValues,
        updatedby: user?.name,
        createdat: undefined,
        updatedat: undefined,
      },
    ], formValues?.name ?? undefined);

    if (actionStatus === "SUCCESS") {
      reset(formValues);
      if (location.prevLocation === "/applications") {
        navigate("/applications");
      } else {
        setAppState((prev) => ({
          ...prev,
          selectedApplication: formValues as NewApplication,
        }));
      }
    }
  };

  const onCancel = () => {
    navigate(
      location.prevLocation === "/tenants/edit"
        ? "/tenants/edit"
        : "/applications"
    );
  };

  const handleModalProps = (isCancelButton?: boolean) => {
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: isCancelButton
        ? t("Applications.cancelModalTitle")
        : t("Applications.saveModalTitle"),
      content: isCancelButton
        ? t("Applications.cancelModalContent")
        : t("Applications.saveModalContent"),
      onConfirm: isCancelButton ? onCancel : onSave,
    }));
  };

  // copy to clipboard action
  useEffect(() => {
    const buttonCopy = document.querySelector("i.iu-icon-copy") as HTMLElement;
    buttonCopy.setAttribute("id", "copy-button-admin-add")
    if (buttonCopy) {
      buttonCopy.addEventListener("click", function () {
        const id = selectedApplication?.id || '';
        navigator.clipboard.writeText(id);
      });
    }
  }, []);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={() => navigate("/applications")}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {t("Applications.editApplicationTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Applications.editApplicationTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Applications.applicationsBreadcrumbLabel"),
              style: { fontWeight: "bold" },
              disabled: true
            },
            {
              label: t("Applications.applicationAdminTitle"),
              command: () => navigate("/applications"),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: t("Applications.editApplicationTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <BoxFull
        className="py-5"
        variant="tertiary"
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
        }} className="wrapper-form-application"
        >
          <p
            id="labelNewApplication"
            className="md"
            dangerouslySetInnerHTML={{ __html: t('Applications.updateApplicationDescriptionLabel') }}
            style={{ lineHeight: 'var(--space-m)' }}
          />
          <div className="form-container md py-5 w-full ml-1">
            <div className="form md w-full">
              <div className="flex flex-wrap">
                {Object.keys(applicationCreatorDefaultValues).filter(key => key != 'name').map((key) => {
                  return (
                    <div
                      key={`c-${key}`}
                      className="flex flex-column mb-3 px-6"
                      style={{ width: "50%" }}
                    >
                      <Controller
                        key={key}
                        name={key as any}
                        control={control}
                        rules={(ApplicationCreatorSchema(t) as any)[key]}
                        render={({ field }) => {
                          return ['name', 'id', 'tenantid', 'segment', 'currentversion', 'createdat', 'updatedat'].includes(key) ? (
                            <div className='app-fields' style={{ paddingLeft: '4rem' }}>

                              <InputText
                                id={`input-label-${key}`}
                                label={t(`Applications.${key === 'tenantid' ? "tenantname" : key}Label`)}
                                placeholder={t(`Applications.${key}Placeholder`)}
                                //@ts-ignore
                                className={errors[key]?.message ? "p-invalid" : `mb-3 mt-2 ${key !== 'name' && 'rd-only'}`}
                                type="text"
                                autoComplete="off"
                                keyfilter={ApplicationCreatorSchema(t)?.name?.keyfilter}
                                maxLength={ApplicationCreatorSchema(t)?.name?.maxLength?.value}
                                {...field}
                                isReadOnly={key !== 'name' ? true : false}
                                tooltipIdRef={["segment", "currentversion"].includes(key) ? `tooltip-${key}` : undefined}
                                tooltipRef={["segment", "currentversion"].includes(key) ? {
                                  content: t(`Applications.${key}Tooltip`)
                                } : undefined}
                                hasTooltipRef={["segment", "currentversion"].includes(key)}
                                hasIcon={key === 'id'}
                                iconPosition={key === 'id' ? "right" : undefined}
                                iconName={key === 'id' ? "iu iu-icon-copy" : undefined}
                              />
                            </div>
                          ) : (
                            <div className='app-fields' style={{ paddingLeft: '4rem' }}>
                              <Dropdown
                                id={`dropdown-${key}`}
                                label={t(`Applications.${key === 'tenantid' ? "tenantname" : key}Label`)}
                                placeholder={t(`Applications.${key === 'tenantid' ? "tenantname" : key}Placeholder`)}
                                options={key === 'status'
                                  ? APPLICATION_STATES(t)
                                  : formOptions?.[
                                  (key === "tenantid" ? "tenants" : "") ||
                                  (key === "segment" ? "segments" : "") ||
                                  (key === "currentversion" ? "versions" : "") ||
                                  (key === "installedversion" ? "versions" : "")
                                  ]}
                                filter={false}
                                emptyFilterMessage={t(`Applications.emptyLabel`)}
                                emptyMessage={t(`Applications.emptyLabel`)}
                                style={{
                                  zIndex: "6000 !important",
                                }}
                                {...field}
                                showClear={field.value ? true : false}
                                onChange={(e: any) => {
                                  setValue(field.name, e.value || null, REVALIDATE_FORM_PROPS);
                                }}
                                //@ts-ignore
                                className={errors[key]?.message ? "p-invalid " : "dropdown"}
                              />

                            </div>
                          )
                        }}
                      />
                      {['status'].includes(key) &&
                        <ErrorMessage
                          errors={errors}
                          //@ts-ignore
                          name={key}
                          //@ts-ignore
                          render={({ message }) => (
                            <small
                              className="p-error block"
                              style={{ marginTop: "0.5rem", marginBottom: "1rem", marginLeft: "4rem" }}
                            >
                              {t(message)}
                            </small>
                          )}
                        />}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
          <p id="objetiveApplicationLabel" className="md subtitle">
            {t('Applications.ObjectiveApplicationLabel')}
            <i className=" iu iu-icon-help act-btn" onClick={() => { setVisibleModelInformationApp(true) }} />
          </p>
          <div className="form-container md py-5 w-full ml-1">
            <div className="form md w-full">
              <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">

                {Object.keys(applicationCreatorDefaultValues).filter(key => key == 'name').map((key) => {
                  return (
                    <div
                      key={`c-${key}`}
                      className="flex flex-column mb-3 px-6"
                      style={{ width: "50%" }}
                    >
                      <Controller
                        key={key}
                        name={key as any}
                        control={control}
                        rules={(ApplicationCreatorSchema(t) as any)[key]}
                        render={({ field }) => {
                          return ['name'].includes(key) ? (
                            <div className='app-fields' style={{ paddingLeft: '4rem' }}>
                              <InputText
                                id={`input-label-${key}`}
                                label={t(`Applications.${key}Label`)}
                                placeholder={t(`Applications.${key}Placeholder`)}
                                className={errors.name?.message ? "p-invalid" : "mb-3 mt-2"}
                                type="text"
                                autoComplete="off"
                                keyfilter={ApplicationCreatorSchema(t)?.name?.keyfilter}
                                maxLength={ApplicationCreatorSchema(t)?.name?.maxLength?.value}
                                {...field}
                              />
                            </div>
                          ) : (
                            <div className='app-fields' style={{ paddingLeft: '4rem' }}>
                              <Dropdown
                                id={`dropdown-${key}`}
                                className="dropdown mb-3"
                                label={t(`Applications.${key === 'tenantid' ? "tenantname" : key}Label`)}
                                placeholder={t(`Applications.${key === 'tenantid' ? "tenantname" : key}Placeholder`)}
                                options={formOptions?.[
                                  (key === "tenantid" ? "tenants" : "") ||
                                  (key === "segment" ? "segments" : "") ||
                                  (key === "currentversion" ? "versions" : "") ||
                                  (key === "installedversion" ? "versions" : "")
                                ]}
                                filter={false}
                                style={{
                                  zIndex: "6000 !important",
                                }}
                                {...field}
                                showClear={field.value ? true : false}
                                onChange={(e: any) => {
                                  setValue(field.name, e.value || null, REVALIDATE_FORM_PROPS);
                                }}
                              />
                            </div>
                          )
                        }}
                      />
                      {['name'].includes(key) &&
                        <ErrorMessage
                          errors={errors}
                          name="name"
                          //@ts-ignore
                          render={({ message }) => (
                            <small
                              className="p-error block"
                              style={{ marginTop: "0.5rem", marginBottom: "1rem", marginLeft: "4rem" }}
                            >
                              {message}
                            </small>
                          )}
                        />}
                    </div>
                  )
                })}
                <div
                  className="flex flex-row mb-3 px-6"
                  style={{ width: "50%" }}
                >
                  <i className="help-name-application iu iu-icon-info act-btn mr-2" />
                  <p className='text-description-name'>
                    <div dangerouslySetInnerHTML={{ __html: t('Applications.DescriptionObjectiveApplicationText') }} />
                  </p>
                </div>

              </div>
            </div>
          </div>
          <p id="objetiveApplicationLabel" className="md subtitle" style={{ marginBottom: '0px' }}>
            {t('Environments.environmentsTitle')}
          </p>
          <p id="labelNewApplication" className="md ml-5 mt-5" >
            <div dangerouslySetInnerHTML={{ __html: t('Applications.DescriptionEnvironmentText') }} />
          </p>
        </div>

        <AppDataTableActions
          options={formOptions}
          auxModalController={setModalProps}
        />
      </BoxFull>

      <BoxFull className="pt-0 px-4 pb-5" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            variant="p-button-link"
            label={t("Applications.cancelBtnLabel")}
            onClick={!isDirty ? onCancel : () => handleModalProps(true)}
          />
          <Button
            variant="p-button"
            label={t("Applications.saveBtnLabel")}
            disabled={!(isDirty && isValid)}
            onClick={() => handleModalProps()}
          />
        </div>
      </BoxFull>
      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
      <InformationApplication visible={visibleModelInformationApp}
        closeHandler={() => { setVisibleModelInformationApp(false) }} />
    </>
  );
};
